import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../../../services/ApiService';
import Modal from '../../ui/Modal';
import Button from '../../ui/Button';
import { useToast } from '../../../contexts/ToastContext';
import type {
  PerformanceRecord,
  PerformanceRecordInput,
  PerformanceRecordType,
} from '../../../types/buildExpansion';

/**
 * Owner create/edit form for a performance record. Field behavior is driven by
 * the record type's allowed units (mirrors backend perfValidateInput):
 * - time types (units contain "s"): one text field accepting "1:10.177" or "70.177"
 * - speed/distance/power types: numeric value + unit select
 * - dyno: full dyno field set with a REQUIRED wheel/crank measurement location
 */

export interface PerfRecordTypeMeta {
  key: string;
  label: string;
  units: string[];
  direction: string;
}

/** Mirrors backend perfRecordTypes(); used until the API metadata loads. */
const FALLBACK_RECORD_TYPES: PerfRecordTypeMeta[] = [
  { key: 'dyno', label: 'Dyno', units: ['hp', 'kw'], direction: 'higher' },
  { key: 'lap_time', label: 'Lap time', units: ['s'], direction: 'lower' },
  { key: 'quarter_mile', label: 'Quarter mile', units: ['s'], direction: 'lower' },
  { key: 'eighth_mile', label: 'Eighth mile', units: ['s'], direction: 'lower' },
  { key: 'half_mile', label: 'Half mile', units: ['s'], direction: 'lower' },
  { key: 'standing_mile', label: 'Standing mile', units: ['mph', 'kph'], direction: 'higher' },
  { key: 'top_speed', label: 'Top speed', units: ['mph', 'kph'], direction: 'higher' },
  { key: 'autocross', label: 'Autocross', units: ['s'], direction: 'lower' },
  { key: 'rally_stage', label: 'Rally stage', units: ['s'], direction: 'lower' },
  { key: 'hill_climb', label: 'Hill climb', units: ['s'], direction: 'lower' },
  { key: 'drag_speed', label: 'Drag trap speed', units: ['mph', 'kph'], direction: 'higher' },
  { key: 'braking', label: 'Braking', units: ['ft', 'm'], direction: 'lower' },
  { key: 'acceleration', label: 'Acceleration', units: ['s'], direction: 'lower' },
  { key: 'custom', label: 'Sanctioned result', units: [], direction: 'higher' },
];

const CORRECTION_FACTORS = [
  { value: '', label: 'Not specified' },
  { value: 'SAE', label: 'SAE' },
  { value: 'STD', label: 'STD' },
  { value: 'uncorrected', label: 'Uncorrected' },
];

/** Same acceptance rule as backend perfParseTimeToSeconds. */
const isValidTimeInput = (raw: string): boolean => {
  const value = raw.trim();
  if (value === '') return false;
  if (/^\d+(\.\d+)?$/.test(value)) {
    const n = parseFloat(value);
    return n > 0 && n <= 100000;
  }
  return /^(?:\d+:)?\d{1,2}:\d{1,2}(?:\.\d+)?$/.test(value);
};

const isPositiveNumber = (raw: string, max = 100000): boolean => {
  const value = raw.trim();
  if (value === '' || !/^\d+(\.\d+)?$/.test(value)) return false;
  const n = parseFloat(value);
  return n > 0 && n <= max;
};

interface FormState {
  record_type: string;
  value: string;
  unit_primary: string;
  torque: string;
  measurement_location: '' | 'wheel' | 'crank';
  dyno_type: string;
  correction_factor: string;
  fuel: string;
  boost_psi: string;
  tuner_name: string;
  tune_revision: string;
  title: string;
  venue_name: string;
  layout_name: string;
  date_recorded: string;
  result_position: string;
  field_size: string;
  sanctioning_body: string;
  timing_method: string;
  notes: string;
  is_public: boolean;
}

const emptyForm = (recordType = 'lap_time'): FormState => ({
  record_type: recordType,
  value: '',
  unit_primary: '',
  torque: '',
  measurement_location: '',
  dyno_type: '',
  correction_factor: '',
  fuel: '',
  boost_psi: '',
  tuner_name: '',
  tune_revision: '',
  title: '',
  venue_name: '',
  layout_name: '',
  date_recorded: '',
  result_position: '',
  field_size: '',
  sanctioning_body: '',
  timing_method: '',
  notes: '',
  is_public: true,
});

const formFromRecord = (record: PerformanceRecord): FormState => ({
  record_type: record.record_type,
  value:
    record.display_value != null && record.record_type !== 'dyno'
      ? record.display_value
      : record.value_primary != null
        ? String(record.value_primary)
        : '',
  unit_primary: record.unit_primary ?? '',
  torque: record.value_secondary != null ? String(record.value_secondary) : '',
  measurement_location: record.measurement_location ?? '',
  dyno_type: record.dyno_type ?? '',
  correction_factor: record.correction_factor ?? '',
  fuel: record.fuel ?? '',
  boost_psi: record.boost_psi != null ? String(record.boost_psi) : '',
  tuner_name: record.tuner_name ?? '',
  tune_revision: record.tune_revision ?? '',
  title: record.title ?? '',
  venue_name: record.venue_name ?? '',
  layout_name: record.layout_name ?? '',
  date_recorded: record.date_recorded ?? '',
  result_position: record.result_position != null ? String(record.result_position) : '',
  field_size: record.field_size != null ? String(record.field_size) : '',
  sanctioning_body: record.sanctioning_body ?? '',
  timing_method: record.timing_method ?? '',
  notes: record.notes ?? '',
  is_public: record.is_public,
});

export interface PerformanceRecordFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleId: number;
  /** When set, the modal edits this record; otherwise it creates a new one. */
  record?: PerformanceRecord | null;
  /** Record-type metadata from getPerformanceRecordList; fetched when omitted. */
  recordTypes?: PerfRecordTypeMeta[];
  onSaved: () => void;
}

export const PerformanceRecordFormModal: React.FC<PerformanceRecordFormModalProps> = ({
  isOpen,
  onClose,
  vehicleId,
  record = null,
  recordTypes,
  onSaved,
}) => {
  const { showToast } = useToast();
  const [form, setForm] = useState<FormState>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState('');
  const [saving, setSaving] = useState(false);
  const [fetchedTypes, setFetchedTypes] = useState<PerfRecordTypeMeta[] | null>(null);

  const types = recordTypes?.length ? recordTypes : fetchedTypes ?? FALLBACK_RECORD_TYPES;

  useEffect(() => {
    if (!isOpen) return;
    setForm(record ? formFromRecord(record) : emptyForm());
    setErrors({});
    setGeneralError('');
  }, [isOpen, record]);

  useEffect(() => {
    if (!isOpen || recordTypes?.length || fetchedTypes) return;
    let cancelled = false;
    apiService
      .getPerformanceRecordList(vehicleId)
      .then(({ record_types }) => {
        if (!cancelled && record_types.length > 0) setFetchedTypes(record_types);
      })
      .catch(() => {
        // Fallback list already mirrors the backend registry.
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, recordTypes, fetchedTypes, vehicleId]);

  const typeMeta = useMemo(
    () => types.find((t) => t.key === form.record_type) ?? null,
    [types, form.record_type]
  );
  const isDyno = form.record_type === 'dyno';
  const isTimeType = !isDyno && (typeMeta?.units ?? []).includes('s');
  const hasUnits = (typeMeta?.units ?? []).length > 0;

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  const handleTypeChange = (nextType: string) => {
    setForm((prev) => ({
      ...prev,
      record_type: nextType,
      value: '',
      unit_primary: '',
      measurement_location: nextType === 'dyno' ? prev.measurement_location : '',
    }));
    setErrors({});
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!typeMeta) {
      next.record_type = 'Choose a record type';
    }
    const value = form.value.trim();
    if (isDyno) {
      if (!isPositiveNumber(value)) {
        next.value = 'Enter the power number, for example 412';
      }
      if (form.measurement_location !== 'wheel' && form.measurement_location !== 'crank') {
        next.measurement_location = 'Choose wheel or crank. Dyno records require this.';
      }
      if (form.torque.trim() !== '' && !isPositiveNumber(form.torque)) {
        next.torque = 'Torque must be a positive number';
      }
      if (form.boost_psi.trim() !== '') {
        const boost = parseFloat(form.boost_psi);
        if (!/^\d+(\.\d+)?$/.test(form.boost_psi.trim()) || boost < 0 || boost > 200) {
          next.boost_psi = 'Boost must be between 0 and 200 psi';
        }
      }
    } else if (isTimeType) {
      if (!isValidTimeInput(value)) {
        next.value = 'Enter a time like 1:10.177 or 70.177';
      }
    } else if (hasUnits) {
      if (!isPositiveNumber(value)) {
        next.value = 'Enter a positive number';
      }
    } else if (value !== '' && !isPositiveNumber(value)) {
      next.value = 'Value must be a positive number';
    }

    if (form.result_position.trim() !== '') {
      const pos = parseInt(form.result_position, 10);
      if (!/^\d+$/.test(form.result_position.trim()) || pos < 1 || pos > 10000) {
        next.result_position = 'Position must be between 1 and 10,000';
      }
    }
    if (form.field_size.trim() !== '') {
      const size = parseInt(form.field_size, 10);
      if (!/^\d+$/.test(form.field_size.trim()) || size < 1 || size > 100000) {
        next.field_size = 'Field size must be between 1 and 100,000';
      }
    }
    if (form.date_recorded) {
      const ts = new Date(form.date_recorded).getTime();
      if (Number.isNaN(ts) || ts > Date.now() + 86400000) {
        next.date_recorded = 'Date cannot be in the future';
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setGeneralError('');
    if (!validate()) return;

    const value = form.value.trim();
    const input: PerformanceRecordInput = {
      vehicle_id: vehicleId,
      record_type: form.record_type as PerformanceRecordType,
      title: form.title.trim() || undefined,
      // Time inputs go up as strings ("1:10.177"); the backend normalizes them.
      value_primary: value === '' ? undefined : isTimeType ? value : parseFloat(value),
      unit_primary: isTimeType
        ? 's'
        : form.unit_primary || (typeMeta?.units[0] ?? undefined),
      venue_name: form.venue_name.trim() || undefined,
      layout_name: form.layout_name.trim() || undefined,
      date_recorded: form.date_recorded || undefined,
      result_position: form.result_position.trim()
        ? parseInt(form.result_position, 10)
        : undefined,
      field_size: form.field_size.trim() ? parseInt(form.field_size, 10) : undefined,
      sanctioning_body: form.sanctioning_body.trim() || undefined,
      timing_method: form.timing_method.trim() || undefined,
      notes: form.notes.trim() || undefined,
      is_public: form.is_public,
    };
    if (isDyno) {
      input.measurement_location = form.measurement_location as 'wheel' | 'crank';
      input.value_secondary = form.torque.trim() ? parseFloat(form.torque) : undefined;
      input.unit_secondary = form.torque.trim() ? 'lb-ft' : undefined;
      input.dyno_type = form.dyno_type.trim() || undefined;
      input.correction_factor = form.correction_factor || undefined;
      input.fuel = form.fuel.trim() || undefined;
      input.boost_psi = form.boost_psi.trim() ? parseFloat(form.boost_psi) : undefined;
      input.tuner_name = form.tuner_name.trim() || undefined;
      input.tune_revision = form.tune_revision.trim() || undefined;
    }

    setSaving(true);
    try {
      if (record) {
        await apiService.updateExpandedPerformanceRecord(record.id, input);
        showToast('Record updated.', 'success');
      } else {
        await apiService.createExpandedPerformanceRecord(input);
        showToast('Record added.', 'success');
      }
      onSaved();
      onClose();
    } catch (err) {
      setGeneralError(err instanceof Error ? err.message : 'Failed to save the record.');
    } finally {
      setSaving(false);
    }
  };

  const valueLabel = isDyno
    ? 'Power *'
    : isTimeType
      ? 'Time *'
      : hasUnits
        ? 'Value *'
        : 'Value';

  const unitOptions = typeMeta?.units ?? [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!saving) onClose();
      }}
      title={record ? 'Edit performance record' : 'Add performance record'}
      description="Log a result for this build. Times, speeds, and dyno numbers each track personal bests."
      size="xl"
      closeOnOverlayClick={!saving}
      closeOnEscape={!saving}
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            className="min-h-[44px]"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="performance-record-form"
            variant="primary"
            loading={saving}
            loadingLabel="Saving…"
            className="min-h-[44px]"
          >
            {record ? 'Save changes' : 'Add record'}
          </Button>
        </div>
      }
    >
      <div
        className="mb-4 p-3 rounded-lg border border-sky-500/25 bg-sky-500/10 text-sky-200 text-sm"
        role="note"
      >
        New records are labeled Owner reported until verified.
      </div>

      {generalError && (
        <div
          className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm"
          role="alert"
        >
          {generalError}
        </div>
      )}

      <form id="performance-record-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
        <div>
          <label htmlFor="perf-record-type" className="input-label">
            Record type *
          </label>
          <select
            id="perf-record-type"
            value={form.record_type}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="input min-h-[44px]"
            disabled={saving || Boolean(record)}
            aria-invalid={Boolean(errors.record_type)}
            aria-describedby={errors.record_type ? 'perf-record-type-error' : undefined}
          >
            {types.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
          {record && (
            <p className="mt-1 text-xs text-gray-500">
              The record type cannot change after creation.
            </p>
          )}
          {errors.record_type && (
            <p id="perf-record-type-error" className="mt-1 text-sm text-red-400">
              {errors.record_type}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="perf-value" className="input-label">
              {valueLabel}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode={isTimeType ? 'text' : 'decimal'}
                id="perf-value"
                value={form.value}
                onChange={(e) => setField('value', e.target.value)}
                className={`input min-h-[44px] flex-1 ${errors.value ? 'border-red-500' : ''}`}
                placeholder={isDyno ? 'e.g. 412' : isTimeType ? 'e.g. 1:10.177 or 70.177' : 'e.g. 158'}
                disabled={saving}
                aria-invalid={Boolean(errors.value)}
                aria-describedby={errors.value ? 'perf-value-error' : 'perf-value-help'}
              />
              {!isTimeType && unitOptions.length > 0 && (
                <select
                  value={form.unit_primary || unitOptions[0]}
                  onChange={(e) => setField('unit_primary', e.target.value)}
                  className="input min-h-[44px] w-24"
                  disabled={saving}
                  aria-label="Unit"
                >
                  {unitOptions.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {errors.value ? (
              <p id="perf-value-error" className="mt-1 text-sm text-red-400">
                {errors.value}
              </p>
            ) : (
              <p id="perf-value-help" className="mt-1 text-xs text-gray-500">
                {isTimeType
                  ? 'Accepts minutes:seconds like 1:10.177 or plain seconds like 70.177.'
                  : isDyno
                    ? 'Peak power number from the dyno sheet.'
                    : hasUnits
                      ? 'Numeric value only.'
                      : 'Optional numeric value for this result.'}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="perf-title" className="input-label">
              Title
            </label>
            <input
              type="text"
              id="perf-title"
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              maxLength={160}
              className="input min-h-[44px]"
              placeholder="Optional short label"
              disabled={saving}
            />
          </div>
        </div>

        {isDyno && (
          <fieldset className="rounded-lg border border-white/[0.06] p-4 space-y-4">
            <legend className="px-1 text-sm font-semibold text-gray-200">Dyno details</legend>

            <fieldset
              aria-describedby={
                errors.measurement_location
                  ? 'perf-measurement-error'
                  : 'perf-measurement-help'
              }
            >
              <legend className="input-label">
                Measurement location * (required)
              </legend>
              <div className="flex gap-4" role="radiogroup" aria-label="Measurement location">
                {(['wheel', 'crank'] as const).map((loc) => (
                  <label
                    key={loc}
                    className="inline-flex items-center gap-2 min-h-[44px] cursor-pointer text-sm text-gray-200"
                  >
                    <input
                      type="radio"
                      name="measurement_location"
                      value={loc}
                      checked={form.measurement_location === loc}
                      onChange={() => setField('measurement_location', loc)}
                      className="checkbox-control"
                      disabled={saving}
                    />
                    {loc === 'wheel' ? 'Wheel (whp)' : 'Crank (bhp)'}
                  </label>
                ))}
              </div>
              {errors.measurement_location ? (
                <p id="perf-measurement-error" className="mt-1 text-sm text-red-400">
                  {errors.measurement_location}
                </p>
              ) : (
                <p id="perf-measurement-help" className="mt-1 text-xs text-gray-500">
                  Wheel and crank numbers are never compared against each other.
                </p>
              )}
            </fieldset>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="perf-torque" className="input-label">
                  Torque (lb-ft)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  id="perf-torque"
                  value={form.torque}
                  onChange={(e) => setField('torque', e.target.value)}
                  className={`input min-h-[44px] ${errors.torque ? 'border-red-500' : ''}`}
                  placeholder="e.g. 380"
                  disabled={saving}
                  aria-invalid={Boolean(errors.torque)}
                  aria-describedby={errors.torque ? 'perf-torque-error' : undefined}
                />
                {errors.torque && (
                  <p id="perf-torque-error" className="mt-1 text-sm text-red-400">
                    {errors.torque}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="perf-dyno-type" className="input-label">
                  Dyno type
                </label>
                <input
                  type="text"
                  id="perf-dyno-type"
                  value={form.dyno_type}
                  onChange={(e) => setField('dyno_type', e.target.value)}
                  maxLength={60}
                  className="input min-h-[44px]"
                  placeholder="e.g. Dynojet 248x"
                  disabled={saving}
                />
              </div>
              <div>
                <label
                  htmlFor="perf-correction"
                  className="input-label"
                >
                  Correction factor
                </label>
                <select
                  id="perf-correction"
                  value={form.correction_factor}
                  onChange={(e) => setField('correction_factor', e.target.value)}
                  className="input min-h-[44px]"
                  disabled={saving}
                >
                  {CORRECTION_FACTORS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="perf-fuel" className="input-label">
                  Fuel
                </label>
                <input
                  type="text"
                  id="perf-fuel"
                  value={form.fuel}
                  onChange={(e) => setField('fuel', e.target.value)}
                  maxLength={60}
                  className="input min-h-[44px]"
                  placeholder="e.g. 93 octane, E85"
                  disabled={saving}
                />
              </div>
              <div>
                <label htmlFor="perf-boost" className="input-label">
                  Boost (psi)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  id="perf-boost"
                  value={form.boost_psi}
                  onChange={(e) => setField('boost_psi', e.target.value)}
                  className={`input min-h-[44px] ${errors.boost_psi ? 'border-red-500' : ''}`}
                  placeholder="e.g. 22"
                  disabled={saving}
                  aria-invalid={Boolean(errors.boost_psi)}
                  aria-describedby={errors.boost_psi ? 'perf-boost-error' : undefined}
                />
                {errors.boost_psi && (
                  <p id="perf-boost-error" className="mt-1 text-sm text-red-400">
                    {errors.boost_psi}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="perf-tuner" className="input-label">
                  Tuner name
                </label>
                <input
                  type="text"
                  id="perf-tuner"
                  value={form.tuner_name}
                  onChange={(e) => setField('tuner_name', e.target.value)}
                  maxLength={120}
                  className="input min-h-[44px]"
                  placeholder="Who tuned it"
                  disabled={saving}
                />
              </div>
              <div>
                <label htmlFor="perf-tune-rev" className="input-label">
                  Tune revision
                </label>
                <input
                  type="text"
                  id="perf-tune-rev"
                  value={form.tune_revision}
                  onChange={(e) => setField('tune_revision', e.target.value)}
                  maxLength={60}
                  className="input min-h-[44px]"
                  placeholder="e.g. v3 map"
                  disabled={saving}
                />
              </div>
            </div>
          </fieldset>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="perf-venue" className="input-label">
              Venue name
            </label>
            <input
              type="text"
              id="perf-venue"
              value={form.venue_name}
              onChange={(e) => setField('venue_name', e.target.value)}
              maxLength={160}
              className="input min-h-[44px]"
              placeholder="e.g. Laguna Seca"
              disabled={saving}
            />
          </div>
          <div>
            <label htmlFor="perf-layout" className="input-label">
              Layout name
            </label>
            <input
              type="text"
              id="perf-layout"
              value={form.layout_name}
              onChange={(e) => setField('layout_name', e.target.value)}
              maxLength={120}
              className="input min-h-[44px]"
              placeholder="e.g. Full course"
              disabled={saving}
              aria-describedby="perf-layout-help"
            />
            <p id="perf-layout-help" className="mt-1 text-xs text-gray-500">
              Personal bests are tracked per layout.
            </p>
          </div>
          <div>
            <label htmlFor="perf-date" className="input-label">
              Date recorded
            </label>
            <input
              type="date"
              id="perf-date"
              value={form.date_recorded}
              onChange={(e) => setField('date_recorded', e.target.value)}
              className={`input min-h-[44px] ${errors.date_recorded ? 'border-red-500' : ''}`}
              disabled={saving}
              aria-invalid={Boolean(errors.date_recorded)}
              aria-describedby={errors.date_recorded ? 'perf-date-error' : undefined}
            />
            {errors.date_recorded && (
              <p id="perf-date-error" className="mt-1 text-sm text-red-400">
                {errors.date_recorded}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="perf-position" className="input-label">
                Result position
              </label>
              <input
                type="text"
                inputMode="numeric"
                id="perf-position"
                value={form.result_position}
                onChange={(e) => setField('result_position', e.target.value)}
                className={`input min-h-[44px] ${errors.result_position ? 'border-red-500' : ''}`}
                placeholder="e.g. 3"
                disabled={saving}
                aria-invalid={Boolean(errors.result_position)}
                aria-describedby={errors.result_position ? 'perf-position-error' : undefined}
              />
              {errors.result_position && (
                <p id="perf-position-error" className="mt-1 text-sm text-red-400">
                  {errors.result_position}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="perf-field-size" className="input-label">
                Field size
              </label>
              <input
                type="text"
                inputMode="numeric"
                id="perf-field-size"
                value={form.field_size}
                onChange={(e) => setField('field_size', e.target.value)}
                className={`input min-h-[44px] ${errors.field_size ? 'border-red-500' : ''}`}
                placeholder="e.g. 24"
                disabled={saving}
                aria-invalid={Boolean(errors.field_size)}
                aria-describedby={errors.field_size ? 'perf-field-size-error' : undefined}
              />
              {errors.field_size && (
                <p id="perf-field-size-error" className="mt-1 text-sm text-red-400">
                  {errors.field_size}
                </p>
              )}
            </div>
          </div>
          <div>
            <label htmlFor="perf-sanctioning" className="input-label">
              Sanctioning body
            </label>
            <input
              type="text"
              id="perf-sanctioning"
              value={form.sanctioning_body}
              onChange={(e) => setField('sanctioning_body', e.target.value)}
              maxLength={120}
              className="input min-h-[44px]"
              placeholder="e.g. NASA, SCCA"
              disabled={saving}
            />
          </div>
          <div>
            <label htmlFor="perf-timing" className="input-label">
              Timing method
            </label>
            <input
              type="text"
              id="perf-timing"
              value={form.timing_method}
              onChange={(e) => setField('timing_method', e.target.value)}
              maxLength={60}
              className="input min-h-[44px]"
              placeholder="e.g. Transponder, GPS"
              disabled={saving}
            />
          </div>
        </div>

        <div>
          <label htmlFor="perf-notes" className="input-label">
            Notes
          </label>
          <textarea
            id="perf-notes"
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            maxLength={2000}
            rows={3}
            className="input resize-none"
            placeholder="Conditions, setup, anything worth remembering"
            disabled={saving}
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="perf-is-public"
            checked={form.is_public}
            onChange={(e) => setField('is_public', e.target.checked)}
            className="checkbox-control"
            disabled={saving}
          />
          <label htmlFor="perf-is-public" className="ml-2 text-sm text-gray-300 min-h-[44px] flex items-center">
            Show this record on the public build page
          </label>
        </div>
      </form>
    </Modal>
  );
};

export default PerformanceRecordFormModal;
